import {
  Component,
  inject,
  signal,
  computed,
  input,
  output,
  effect,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import {
  FileUploaderComponent
} from '../shared/file-uploader/file-uploader.component';
import { CargaMasivaRepository } from 'src/app/domain/repository/carga-masiva.repository';
import {
  CargaMasivaResumen,
  FilaResultado
} from 'src/app/domain/model/carga-masiva/carga-masiva';
import { cancelarCitasMasivasResultadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseCargaMasivaDialog } from './base-carga-masiva-dialog';
import { CargaMasivaResultadoComponent } from './carga-masiva-resultado.component';

/**
 * Resultado del diálogo de cancelación masiva
 */
export interface CancelarCitasMasivasResult {
  /** Indica si se procesó el archivo */
  procesado: boolean;
  /** Resumen del procesamiento */
  resumen?: CargaMasivaResumen;
}

/**
 * CancelarCitasMasivasDialogComponent
 *
 * Diálogo para cancelar citas masivamente desde un archivo Excel.
 * Replica la funcionalidad del legacy LectorCancelaCita.java
 *
 * El archivo se envía directamente al backend que realiza:
 * - Parseo del Excel
 * - Validación de datos
 * - Cancelación de citas
 *
 * @example
 * ```html
 * <app-cancelar-citas-masivas-dialog
 *   [visible]="showDialog()"
 *   (onClose)="closeDialog()"
 *   (onResult)="handleResult($event)">
 * </app-cancelar-citas-masivas-dialog>
 * ```
 *
 * @author GIROP Migration
 * @since 2.0
 */
@Component({
  selector: 'app-cancelar-citas-masivas-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    MessageModule,
    TooltipModule,
    ProgressBarModule,
    TableModule,
    TagModule,
    FileUploaderComponent,
    CargaMasivaResultadoComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="'Cancelación Masiva de Citas'"
      [visible]="dialogVisible()"
      [modal]="true"
      [closable]="!procesando()"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (visibleChange)="onDialogVisibleChange($event)"
    >
      <!-- Estado inicial: Carga de archivo -->
      @if (estado() === 'cargar') {
        <div class="mb-4">
          <p-message
            severity="warn"
            text="Esta acción cancelará múltiples citas. Verifique los datos antes de procesar."
            styleClass="mb-4 w-full"
          >
          </p-message>

          <p class="text-gray-600 mb-4">
            Seleccione un archivo Excel (.xls o .xlsx) con los consecutivos de
            las citas a cancelar y el motivo de cancelación.
          </p>

          <app-file-uploader
            #fileUploader
            [templateUrl]="plantillaUrl"
            [templateFileName]="'Plantilla_CancelacionCita.xlsx'"
            [maxFileSize]="5 * 1024 * 1024"
            (onFileSelected)="onFileSelected($event)"
            (onFileRemoved)="onFileRemoved()"
            (onError)="onFileError($event)"
          >
          </app-file-uploader>
        </div>

        <!-- Mensaje de error -->
        @if (error()) {
          <p-message
            severity="error"
            [text]="error()!"
            styleClass="mt-4 w-full"
          >
          </p-message>
        }

        <!-- Resultados del procesamiento -->
        @if (resultado()) {
          <app-carga-masiva-resultado
            [resumen]="resultado()!"
            [tab]="tabActivo()"
            [onSetTab]="setTabActivo"
            [tableConfig]="resultadosTableConfig()"
            [labels]="resultadoLabels"
          ></app-carga-masiva-resultado>
        }

      }

      <!-- Estado procesando -->
      @if (estado() === 'procesando') {
        <div class="text-center py-8">
          <i class="pi pi-spin pi-spinner text-5xl text-red-500 mb-4"></i>
          <p class="text-lg font-semibold">Cancelando citas...</p>
          <p class="text-gray-500">
            El servidor está procesando las cancelaciones
          </p>
          <p-progressBar [mode]="'indeterminate'" styleClass="mt-4 h-2">
          </p-progressBar>
        </div>
      }

      <ng-template pTemplate="footer">
        @if (estado() === 'cargar') {
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            @if (resultado()) {
              <button
                type="button"
                pButton
                label="Cerrar"
                class="p-button-outlined p-button-secondary min-h-[44px]"
                (click)="cerrarConResultado()"
              ></button>
              <button
                type="button"
                pButton
                label="Cargar otro archivo"
                class="p-button-outlined p-button-secondary min-h-[44px]"
                (click)="reiniciar()"
              ></button>
            } @else {
              <button
                type="button"
                pButton
                label="Cerrar"
                class="p-button-outlined p-button-secondary min-h-[44px]"
                (click)="cerrar()"
              ></button>
              <button
                type="button"
                pButton
                label="Cancelar Citas"
                icon="pi pi-times-circle"
                severity="danger"
                class="p-button-danger min-h-[44px]"
                [disabled]="!archivoSeleccionado()"
                [loading]="procesando()"
                (click)="procesar()"
              ></button>
            }
          </div>
        }
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      :host ::ng-deep .p-datatable {
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        overflow: hidden;
      }

      :host ::ng-deep .p-datatable-thead > tr > th {
        background: #f9fafb;
      }

      :host ::ng-deep .tabla-resultados .p-datatable-thead > tr > th {
        background: #f3f4f6;
        color: #111827;
        border-color: #d1d5db;
      }

      :host-context(.dark)
        ::ng-deep
        .tabla-resultados
        .p-datatable-thead
        > tr
        > th {
        background: #1f2937;
        color: #f9fafb;
        border-color: #374151;
      }
    `
  ]
})
export class CancelarCitasMasivasDialogComponent extends BaseCargaMasivaDialog {
  private readonly cargaMasivaRepository = inject(CargaMasivaRepository);

  /** Referencia al componente FileUploader */
  @ViewChild('fileUploader') fileUploader!: FileUploaderComponent;

  // ==================== INPUTS/OUTPUTS ====================

  /** Controla la visibilidad del diálogo (input del padre) */
  visible = input<boolean>(false);

  /** Señal interna para controlar visibilidad del diálogo */
  dialogVisible = signal(false);

  /** Emite cuando el diálogo se cierra */
  onClose = output<void>();

  /** Emite el resultado del procesamiento */
  onResult = output<CancelarCitasMasivasResult>();

  constructor() {
    super();
    // Sincroniza el input visible con la señal interna
    effect(() => {
      this.dialogVisible.set(this.visible());
    });
  }

  readonly resultadosTableConfig = computed(() =>
    cancelarCitasMasivasResultadoPreset.createConfig({
      data: this.filasVisibles() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-resultados',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.procesando(),
        emptyMessage: 'No hay filas para mostrar'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '200px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de resultados de cancelacion masiva de citas',
        announceSelection: false,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly resultadoLabels = {
    total: 'Total citas',
    exitosos: 'Canceladas',
    errores: 'Con errores',
    principal: 'Programación',
    estadoExitoso: 'Cancelada',
    btnTodos: 'Todos',
    btnExitosos: 'Canceladas',
    btnErrores: 'Con errores',
    tituloPrincipal: 'Programación'
  };

  readonly setTabActivo = (tab: 'todos' | 'exitosos' | 'errores'): void => {
    this.tabActivo.set(tab);
  };

  // ==================== CONFIGURACION ====================

  /** URL de la plantilla */
  get plantillaUrl(): string {
    return this.cargaMasivaRepository.getPlantillaUrl('cancelacion');
  }

  // ==================== METODOS ====================

  /**
   * Envía el archivo al backend para procesamiento
   */
  procesar(): void {
    this.procesarArchivo(
      (archivo) => this.cargaMasivaRepository.cancelarCitasMasivamente(archivo),
      {
        logTag: 'CARGA-MASIVA-CANCELACION',
        errorFallbackMessage: 'Error al procesar el archivo'
      }
    );
  }

  getProgramacionValue(fila: Partial<FilaResultado>): number | string {
    return fila.idRegistro ?? fila.orden ?? '-';
  }

  /**
   * Reinicia el diálogo para cargar otro archivo
   */
  reiniciar(): void {
    this.reiniciarEstado(() => {
      if (this.fileUploader) {
        this.fileUploader.clear();
      }
    });
  }

  /**
   * Maneja el cambio de visibilidad del diálogo (desde PrimeNG)
   */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible && !this.procesando()) {
      this.cerrar();
    }
  }

  /**
   * Cierra el diálogo sin resultado
   */
  cerrar(): void {
    if (!this.procesando()) {
      this.dialogVisible.set(false);
      this.reiniciar();
      this.onClose.emit();
    }
  }

  /**
   * Cierra el diálogo con resultado
   */
  cerrarConResultado(): void {
    const res = this.resultado();
    this.onResult.emit({
      procesado: true,
      resumen: res ?? undefined
    });
    this.dialogVisible.set(false);
    this.reiniciar();
    this.onClose.emit();
  }
}
